import * as d3 from 'd3'
import Tooltip from './SummaryToolTip'

const tooltipRadius = 20

export default class SummarySwarm {
    constructor(
        data,
        { chart },
        { colorScale, xScale, xScaleDetailed, yScale },
        callback
    ) {
        this.data = data
        this.chart = chart

        this.xScale = xScale
        this.xScaleDetailed = xScaleDetailed
        this.yScale = yScale
        this.colorScale = colorScale

        this.tooltips = []

        this.callback = callback

        this.throwMax = d3.max(this.data, (d) => d.qualifiedThrows)

        this.radiusScale = d3
            .scaleSqrt()
            .domain([0, this.throwMax])
            .range([2, 8])

        this.nameFilter = ''

        // TODO HANDLE FILTERED DATA
        this.simulation = d3
            .forceSimulation(this.data)
            .force('x', d3.forceX((d) => this.xScale(d.arm)).strength(0.5))
            .force('y', d3.forceY((d) => this.yScale(d.position)).strength(0.1))
            .force(
                'collide',
                d3
                    .forceCollide()
                    .radius((d) => this.radiusScale(d.qualifiedThrows) + 0.5)
                    .strength(0.1)
            )
            .on('tick', this.tick.bind(this))

        this.tooltip = chart
            .append('g')
            .attr('class', 'arm-tooltip')
            .attr('opacity', 0)

        const initialWidth = 30
        this.tooltip
            .append('rect')
            .attr('class', 'max-bubble-line')
            .attr('width', initialWidth)
            .attr('stroke-width', 1)
            .attr('stroke', 'white')
            .attr('height', 4)
            .attr('y', -2)

        this.tooltip
            .append('circle')
            .attr('class', 'tooltip-bubble')
            .attr('r', tooltipRadius)
            .attr('fill', 'rgba(255, 255, 255, 0.9)')
            .attr('stroke-width', 2)

        this.tooltip
            .append('text')
            .attr('class', 'tooltip-bubble-avg-text')
            .attr('fill', '#999')
            .attr('font-size', 9)
            .attr('text-anchor', 'middle')
            .text('AVG')
            .attr('dy', '1.45em')

        this.tooltip
            .append('text')
            .attr('class', 'tooltip-value')
            .attr('dominant-baseline', 'middle')
            .attr('text-anchor', 'middle')
            .attr('font-weight', 'bold')
            .attr('dy', '0px')

        const playerMaxGroup = this.tooltip
            .append('g')
            .attr('class', 'max-bubble-text')
            .attr('x', initialWidth)

        playerMaxGroup
            .append('circle')
            .attr('class', 'max-bubble-circle')
            .attr('r', 10)
            .attr('fill', 'rgba(255, 255, 255, 0.7)')
            .attr('stroke-width', 0)

        playerMaxGroup
            .append('rect')
            .attr('class', 'max-bubble-rect')
            .attr('width', 73)
            .attr('height', 20)
            .attr('y', '-10')
            .attr('fill', 'rgba(255, 255, 255, 0.7)')
            .attr('stroke-width', 0)

        playerMaxGroup
            .append('text')
            .attr('fill', '#999')
            .attr('font-size', 9)
            .attr('dominant-baseline', 'middle')
            .attr('dy', '0')
            .attr('dx', 8)
            .text('PLAYER MAX')

        this.tooltip
            .append('circle')
            .attr('class', 'max-bubble')
            .attr('cx', initialWidth)
            .attr('r', 5)
            .attr('fill', 'black')
            .attr('stroke-width', 2)
            .attr('stroke', '#fff')

        const nameHeight = 30
        const nameWidth = 120
        this.tooltipName = this.tooltip
            .append('g')
            .attr('transform', 'translate(0, 55)')

        this.tooltipName
            .append('rect')
            .attr('class', 'tooltip-name-bg')
            .attr('width', nameWidth)
            .attr('height', nameHeight)
            .attr('rx', 5)
            .attr('x', -nameWidth / 2)
            .attr('fill', '#222')
            .attr('stroke', '#fff')
            .attr('stroke-width', 2)

        this.tooltipName
            .append('text')
            .attr('class', 'tooltip-name')
            .attr('y', nameHeight / 2)
            .attr('dy', '2') // always be nudging
            .attr('dominant-baseline', 'middle')
            .attr('text-anchor', 'middle')
            .attr('stroke-width', 0)
            .attr('fill', '#fff')
    }

    showTooltip(_, d) {
        if (this.locked || this.searchLocked || this.tooltips.length) {
            return
        }

        if (this.isFiltered(d)) {
            return
        }

        this.createTooltips(d.player_id, d.position)
    }

    hideTooltip() {
        if (this.locked || this.searchLocked) {
            return
        }
        this.removeTooltips()
    }

    removeTooltips() {
        this.tooltips.forEach((tip) => tip.remove())
        this.nodes.attr('stroke-width', 0)
        this.tooltips = []
        this.locked = false
    }

    isFiltered({ name }) {
        const normed = name
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
        return !normed.includes(this.nameFilter.toLowerCase())
    }

    update(newData, xScale) {
        this.data = newData
        this.restartSimulation(xScale)
    }

    updatePlayerSearch(name = '', delay = 0) {
        this.nameFilter = name
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()

        this.nodes.attr('opacity', (d) => (this.isFiltered(d) ? 0.1 : 1))
        const uniqueIds = new Set(
            this.data.filter((d) => !this.isFiltered(d)).map((d) => d.player_id)
        )

        if (uniqueIds.size === 1 && !this.tooltips.length) {
            const [playerId] = uniqueIds
            this.searchLocked = true

            if (delay) {
                setTimeout(() => this.createTooltips(playerId), delay)
            } else {
                this.createTooltips(playerId)
            }
        } else if (uniqueIds.size !== 1) {
            this.removeTooltips()
            this.searchLocked = false
        }
    }

    createTooltips(playerId, position) {
        const bubbles = this.data.filter((row) => playerId === row.player_id)
        const bubbleNodes = this.nodes
            .filter((row) => playerId === row.player_id)
            .attr('stroke', 'black')
            .attr('stroke-width', 2)

        const selectedIdx = position
            ? bubbles.findIndex((d) => d.position === position)
            : bubbles.length - 1

        this.selectedPlayer = bubbles[selectedIdx]

        this.tooltips = bubbles.map((bub, i) => {
            const node = bubbleNodes.filter(
                (data) => data.position === bub.position
            )
            return new Tooltip(
                {
                    data: bub,
                    colorScale: this.colorScale,
                    xScale: this.xScale,
                    yScale: this.yScale,
                    node,
                },
                i === selectedIdx
            )
        })
    }

    rescaleTooltip(xScale) {
        if (this.selectedPlayer) {
            this.tooltips.forEach((tip) => {
                tip.$tooltip
                    .transition()
                    .attr(
                        'transform',
                        `translate(${xScale(tip.data.arm)}, ${
                            this.yScale(tip.data.position) - 35
                        })`
                    )

                tip.moveMaxBubble(xScale)
            })
        }
    }

    restartSimulation(xScale) {
        this.simulation.nodes(this.data)
        this.simulation.alpha(0.5)
        this.simulation.force(
            'x',
            d3.forceX((d) => xScale(d.arm)).strength(0.5)
        )
        this.simulation.restart()
    }

    tick() {
        this.nodes = this.chart
            .selectAll('circle.force')
            .data(this.data, (d) => d.player_id + d.position)
            .join(
                (enter) => {
                    const el = enter
                        .append('circle')
                        .attr('class', 'force')
                        .attr('cx', (d) => this.xScale(d.arm))
                        .attr('cy', (d) => this.yScale(d.position))
                        .attr('r', (d) => this.radiusScale(d.qualifiedThrows))
                        .attr('fill', (d) => this.colorScale(d.position))
                        .attr('opacity', 0.1)
                        .style('cursor', 'pointer')
                        .on('mouseover', this.showTooltip.bind(this))
                        .on('mouseout', this.hideTooltip.bind(this))
                        .on('click', async (e, d) => {
                            e.stopPropagation()
                            if (this.locked || this.isFiltered(d)) {
                                return
                            }

                            this.locked = true
                            this.selectedPlayer = d
                            this.nodes
                                .attr('opacity', 0.1)
                                .style('cursor', 'default')
                            d3.select(e.target)
                                .attr('opacity', 1)
                                .attr('stroke', 'black')
                                .attr('stroke-width', 2)
                            await this.callback(d, e.target)
                            d3.select(e.target)
                                .attr('opacity', 0.1)
                                .attr('stroke-width', 0)
                        })
                    el.transition().attr('opacity', 1)

                    return el
                },
                (update) =>
                    update.attr('cx', (d) => d.x).attr('cy', (d) => d.y),
                (exit) =>
                    // Wanted to get fancy here and add a transition, but I can't figure it out.
                    exit.remove()
            )
    }
}
